/**
 * Small preferences store, backed by the browser's localStorage.
 *
 * This is for things the user CHOSE and expects to still be there next
 * time they open the page - which date range they were looking at, that
 * sort order, that column width. It is not for data: the app's data comes
 * from the API.
 *
 * Two rules this module exists to enforce:
 *
 * - **Reading never throws.** Storage can be disabled, full, or hold
 *   corrupt JSON from an older version of the app. A preference that
 *   cannot be read falls back to the default; it must never take the
 *   page down.
 * - **Values are stored as JSON**, so an object can be kept as an object
 *   rather than being flattened into a string and parsed back by hand.
 */

/** Read a preference. Returns null when it is absent or unreadable. */
const readRaw = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw);
  } catch (err) {
    // Private browsing, disabled storage, or corrupt JSON.
    return null;
  }
};

/**
 * Read a preference, falling back when it is missing or unreadable.
 *
 * @param {string} key
 * @param {*}      fallback
 */
export const loadPreference = (key, fallback = null) => {
  const value = readRaw(key);
  if (value === null || value === undefined) return fallback;
  return value;
};

/**
 * Store a preference.
 *
 * @param {string} key
 * @param {*}      value  Anything JSON-serialisable.
 * @returns {boolean}      Whether it was written.
 */
export const savePreference = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (err) {
    // Quota exceeded, or storage disabled. Not worth failing the page
    // over - the preference simply will not be remembered.
    return false;
  }
};

/**
 * Forget a preference.
 *
 * @param {string} key
 */
export const clearPreference = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (err) {
    // Nothing to do - if it cannot be removed it was never there.
  }
};
